import Button from 'components/Buttons/Button';
import ModalComponent from 'components/Common/Modal';
import Render from 'components/Common/Render';
import PayoutMethodPreview from 'components/PayoutManagement/PayoutMethodPreview';
import { ReactComponent as NoPayout } from 'assets/no_payout.svg';
import Payoutoptions from 'components/PayoutManagement/Payoutoptions';
import PaypalForm from 'components/PayoutManagement/forms/PaypalForm';
import ZelleForm from 'components/PayoutManagement/forms/ZelleForm';
import VenmoForm from 'components/PayoutManagement/forms/VenmoForm';
import { PayoutModalPropsType } from 'components/PayoutManagement/types';
import { PayoutMethodEnum } from 'enum/common';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useLazyPayoutMethodsQuery } from 'store/apis/payoutApi';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { RootState } from 'store';
import ChequeForm from 'components/PayoutManagement/forms/ChequeForm';
import CanadianEFT from 'components/PayoutManagement/forms/CanadianEFT';
import { setPayoutMethods } from 'store/slices/payoutSlice';

const PayoutMethods = () => {
  const dispatch = useAppDispatch();

  const selectedBusiness = useAppSelector((state: RootState) => state.businesses.business);
  const defaultPayoutMethod = useAppSelector((state: RootState) => state.payout.payoutMethod);

  const isBusinessInCanada = selectedBusiness?.address?.countryShortCode === 'CA';
  const [
    getPayoutMethods,
    {
      data: payoutMethod,
      isLoading,
      isFetching: isFetchingPayout,
      isSuccess,
      isError,
      error: apiError,
    },
  ] = useLazyPayoutMethodsQuery();

  const [paymentModal, setPaymentModal] = useState<PayoutModalPropsType>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBankForm, setIsBankForm] = useState(false);

  const onHidePaymentModal = () => {
    setPaymentModal(null);
  };

  const renderHeader = useMemo(() => {
    return <>Payout Methods</>;
  }, []);

  const renderFooter = useMemo(
    () => (
      <div className="bg-white pt-4 justify-end md:items-end items-center md:flex-row flex-col-reverse flex gap-2 w-full">
        {' '}
        {paymentModal?.method === PayoutMethodEnum.CANADA_EFT ? (
          <>
            <Button
              label={!isBankForm ? 'Cancel' : 'Go Back'}
              onClick={isBankForm ? () => setIsBankForm(false) : onHidePaymentModal}
              variant="secondary"
              customClass="md:w-[188px] w-[100%] !me:0"
              disabled={isSubmitting}
            />
            <Button
              form={`${paymentModal?.method}-${isBankForm ? 'bank' : 'personal'}`}
              label={
                !isBankForm ? 'Next' : paymentModal?.type === 'add' ? 'Add Method' : 'Update Method'
              }
              type="submit"
              customClass="md:w-[180px] w-[100%] !me:0"
              loading={isSubmitting}
              disabled={isSubmitting}
            />
          </>
        ) : (
          <>
            {' '}
            <Button
              label="Cancel"
              onClick={() => setPaymentModal(null)}
              variant="secondary"
              customClass="md:w-[188px] w-[100%] !me:0"
              disabled={isSubmitting}
            />
            <Button
              form={paymentModal?.method}
              label={paymentModal?.type === 'add' ? 'Add Method' : 'Update Method'}
              type="submit"
              customClass="md:w-[180px] w-[100%] !me:0"
              loading={isSubmitting}
              disabled={isSubmitting}
            />
          </>
        )}
      </div>
    ),
    [paymentModal, isBankForm, isSubmitting, paymentModal?.method],
  );

  const fetchPayoutMethods = async () => {
    try {
      const response = await getPayoutMethods({
        type: PayoutMethodEnum.DEFAULT,
        uuid: selectedBusiness?.uuid || '',
      });
      dispatch(setPayoutMethods(response.data));
    } catch (error) {
      const test: any = apiError;
      toast.error(test?.data?.error);
      dispatch(setPayoutMethods(null));
    } finally {
    }
  };

  const handlePayoutMethodSelection = (method: PayoutMethodEnum) => {
    // @ts-ignore
    setPaymentModal({ ...paymentModal, method });
    if (method === PayoutMethodEnum.CANADA_EFT) {
      setIsBankForm(false);
    }
  };

  const afterSubmit = () => {
    setPaymentModal(null);
    fetchPayoutMethods();
  };

  const renderFormByMethod = (data: PayoutModalPropsType) => {
    switch (data?.method) {
      case PayoutMethodEnum.PAYPAL:
        return (
          <PaypalForm
            data={paymentModal}
            afterSubmit={afterSubmit}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        );
      case PayoutMethodEnum.VENMO:
        return (
          <VenmoForm
            data={paymentModal}
            afterSubmit={afterSubmit}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        );
      case PayoutMethodEnum.ZELLE:
        return (
          <ZelleForm
            data={paymentModal}
            afterSubmit={afterSubmit}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        );
      case PayoutMethodEnum.CANADA_EFT:
        return (
          <CanadianEFT
            data={paymentModal}
            afterSubmit={afterSubmit}
            isBankForm={isBankForm}
            setIsBankForm={setIsBankForm}
            setIsSubmitting={setIsSubmitting}
          />
        );
      case PayoutMethodEnum.CHECK:
        return (
          <ChequeForm
            data={paymentModal}
            afterSubmit={afterSubmit}
            setIsSubmitting={setIsSubmitting}
          />
        );
      default:
        return 'Method not found';
    }
  };

  useEffect(() => {
    if (!!isSuccess) {
      dispatch(setPayoutMethods(payoutMethod));
    }
    if (!!isError) {
      dispatch(setPayoutMethods(null));
    }
  }, [isError, isSuccess]);

  useEffect(() => {
    fetchPayoutMethods();
  }, []);

  return (
    <div className="flex flex-col gap-2 md:gap-4">
      <div>
        <div className="text:xl md:text-xl text-darkBlue">Saved Payout Methods</div>
        <div className="w-full my-4 overflow-visible">
          {!!defaultPayoutMethod || isLoading || isFetchingPayout ? (
            <PayoutMethodPreview
              loading={isLoading || isFetchingPayout}
              defaultPayoutMethod={defaultPayoutMethod}
              setPaymentModal={setPaymentModal}
              setIsBankForm={setIsBankForm}
              afterSubmit={afterSubmit}
            />
          ) : (
            <div className="flex justify-center items-center flex-col w-full gap-3 text-center md:text-start py-4 pb-[120px] md:pb-4">
              <NoPayout className="w-full max-w-[320px] h-auto max-h-[280px]" />
              <p className="text-sm text-gray-600 max-w-md">
                Please provide a payout method to receive funds. Click below to add your preferred
                method and initiate payouts.
              </p>
              <Button
                customClass="w-[280px] hidden md:block"
                label={'+ Add Payout Method'}
                onClick={() => setPaymentModal({ data: null, type: 'add' })}
              />
              <div
                className="md:hidden fixed bottom-0 left-0 right-0 h-[98px] p-5 bg-white z-40 flex items-center"
                style={{ borderTop: '1px solid #EDEDF0' }}
              >
                <button
                  onClick={() => setPaymentModal({ data: null, type: 'add' })}
                  className="w-full h-[44px] rounded-[4px] font-poppins font-medium text-[14px] text-white transition-opacity hover:opacity-90"
                  style={{
                    background: '#0102CE',
                    padding: '12px 20px',
                  }}
                >
                  + Add Payout Method
                </button>
              </div>
            </div>
          )}
        </div>

        <Render when={!!paymentModal}>
          <ModalComponent
            showClose
            renderHeader={renderHeader}
            renderFooter={renderFooter}
            containerClass="md:w-[50vw] md:h-[70vh] h-[70vh] w-[90vw] relative !overflow-hidden"
            onClose={onHidePaymentModal}
          >
            <div className="w-full overflow-auto">
              <div className="w-full">
                {paymentModal?.type !== 'edit' ? (
                  <Payoutoptions
                    onSelect={handlePayoutMethodSelection}
                    paymentModal={paymentModal}
                    isBusinessInCanada={isBusinessInCanada}
                  />
                ) : null}

                {!!paymentModal?.method ? (
                  // Todo: Replace with component(RenderFormByMethod) and make sure state updates as expecte
                  <> {renderFormByMethod(paymentModal)}</>
                ) : null}
              </div>
            </div>
          </ModalComponent>
        </Render>
      </div>
    </div>
  );
};

export default PayoutMethods;
